import type { CastBlock, CastStatus } from '../lib/useCast.js';

/**
 * The pure view-model behind CastButton: what the button says, whether it is
 * pressable, and what to do instead when in-page casting is dead. No React,
 * no DOM — every string the button can print is decided here.
 *
 * The fallback that rescues almost every dead path is Chrome's built-in tab
 * casting: it mirrors the already-rendered tab, so it needs neither the
 * Presentation API nor a secure context, and the cast device never has to
 * reach the page's URL. Every dead end below names it.
 */
export const TAB_CAST = 'Chrome menu → Cast → Cast tab';

export interface CastButtonInput {
  status: CastStatus;
  /** Chrome has seen at least one cast receiver on the network. */
  available: boolean;
  /**
   * True once the first availability check has settled. Before that,
   * "no devices" would be a guess — the button stays quiet instead.
   */
  checked: boolean;
  block: CastBlock;
  error: string | null;
  /** Set when the TV URL is unreachable from a cast device (localhost). */
  blockedReason?: string | null;
}

export interface CastButtonVM {
  label: string;
  title: string;
  /** The workaround line under the button, when there is one to show. */
  note: string | null;
  disabled: boolean;
  live: boolean;
  connecting: boolean;
}

/**
 * A blocked button says why and names the way out. Silently hiding it is
 * worse: the cast API is missing far more often than it is present.
 */
const BLOCK_NOTE: Record<Exclude<CastBlock, null>, string> = {
  insecure: `This button needs HTTPS. Use ${TAB_CAST} instead.`,
  browser: `This browser can’t cast. Open this page in Chrome or Edge on a computer, then ${TAB_CAST}.`,
  mobile: 'Android can’t cast this page. Use Screen Cast in quick settings, or Chrome on a computer.',
  ios: 'iPhone can’t cast this page. Use Control Center → Screen Mirroring → your TV. Keep the phone unlocked.',
  mac: 'Apple TV: Control Center → Screen Mirroring. Chromecast: open this page in Chrome.',
};

export function castButtonModel(input: CastButtonInput): CastButtonVM {
  const casting = input.status === 'casting';
  const connecting = input.status === 'connecting';

  const note = input.block
    ? BLOCK_NOTE[input.block]
    : (input.blockedReason ??
      (input.error
        ? `${input.error} Try ${TAB_CAST}.`
        : input.checked && !input.available && !casting && !connecting
          ? `No cast devices found. Try ${TAB_CAST}.`
          : null));

  const title =
    note ??
    (casting
      ? 'Stop casting to the TV'
      : !input.checked
        ? 'Looking for cast devices…'
        : input.available
          ? 'Cast this table to a TV'
          : 'No cast devices found');

  return {
    label: casting ? 'Casting' : connecting ? 'Connecting…' : 'Cast',
    title,
    note,
    disabled:
      Boolean(input.block) || Boolean(input.blockedReason),
    live: casting,
    connecting,
  };
}
